import React, { useEffect, useState } from "react";
import axios from "../api/axios";
import Filters from "../components/LogsByDay/Filters";
import LogsTable from "../components/LogsByDay/LogsTable";
import Pagination from "../components/LogsByDay/Pagination";
import ErrorBox from "../components/LogsByDay/ErrorBox";

const MAX_MINUTES = 24 * 60; // 24 giờ

export default function LogsByDay() {
  const [health, setHealth] = useState("Đang kiểm tra...");

  const [filters, setFilters] = useState({
    imei: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
  });

  const [page, setPage] = useState(1);  // Trang hiện tại
  const [pageSize, setPageSize] = useState(25);  // Số bản ghi trên mỗi trang
  const [totalRecords, setTotalRecords] = useState(0);  // Tổng số bản ghi từ API

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  /* ================= INIT DEFAULT ================= */
  useEffect(() => {
    const now = new Date();
    const after1h = new Date(now.getTime() + 60 * 60 * 1000);

    setFilters({
      imei: "",
      startDate: now.toISOString().slice(0, 10),
      endDate: now.toISOString().slice(0, 10),
      startTime: now.toTimeString().slice(0, 5),
      endTime: after1h.toTimeString().slice(0, 5),
    });
  }, []);

  /* ================= HEALTH CHECK ================= */
  useEffect(() => {
    axios
      .get("/logs/health")
      .then(() => setHealth("✅ Cassandra"))
      .catch(() => setHealth("❌ Cassandra"));
  }, []);

  /* ================= BUILD PAYLOAD ================= */
  const buildPayload = () => {
    setErrorMsg("");

    const { imei, startDate, startTime, endDate, endTime } = filters;

    if (!imei) {
      setErrorMsg("Vui lòng nhập IMEI.");
      return null;
    }

    if (!startDate || !startTime || !endDate || !endTime) {
      setErrorMsg("Vui lòng chọn đầy đủ ngày và giờ.");
      return null;
    }

    // 👉 Ghép datetime (KHÔNG ép cùng ngày)
    const start = new Date(`${startDate}T${startTime}:00`);
    const end = new Date(`${endDate}T${endTime}:00`);

    const diffMinutes = (end - start) / 60000;

    if (diffMinutes <= 0) {
      setErrorMsg("Thời gian kết thúc phải lớn hơn thời gian bắt đầu.");
      return null;
    }

    if (diffMinutes > MAX_MINUTES) {
      setErrorMsg("Khoảng thời gian tối đa là 24 giờ.");
      return null;
    }

    return {
      imei,
      start_time: start.toISOString(), // UTC (Z)
      end_time: end.toISOString(),
      page,
      page_size: pageSize,
    };
  };

  /* ================= FETCH LOGS ================= */
  const fetchLogs = async () => {
    const payload = buildPayload();
    if (!payload) return;

    try {
      setLoading(true);
      setLogs([]);

      const res = await axios.post("/logs", payload);
      setLogs(res.data || []);
      setTotalRecords(res.data.length);  // Lưu tổng số bản ghi
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Lỗi khi gọi API /logs");
    } finally {
      setLoading(false);
    }
  };

  /* ================= PAGINATION ================= */
  useEffect(() => {
    if (!filters.startDate) return;
    fetchLogs();  // Gọi API khi thay đổi trang hoặc pageSize
  }, [page, pageSize]);

  /* ================= RENDER ================= */
  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 border-b pb-4">
        <div>
          <h1 className="text-2xl font-semibold">Tra cứu lịch sử</h1>
          <p className="text-sm text-gray-600">
            Theo IMEI và khoảng thời gian
          </p>
        </div>
        <span className="text-sm px-3 py-1 rounded-full border">
          {health}
        </span>
      </div>

      <Filters
        filters={filters}
        setFilters={setFilters}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onSubmit={() => {
          setPage(1);
          fetchLogs();
        }}
        loading={loading}
      />

      {errorMsg && <ErrorBox message={errorMsg} />}

      <LogsTable logs={logs} loading={loading} page={page} pageSize={pageSize} />

      <Pagination
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        dataLength={logs.length}
        totalRecords={totalRecords}  // Tổng số bản ghi
        loading={loading}
      />
    </div>
  );
}
